<template>
    <div v-scroll id="app" ref="app" :style="backgroundStyles">
        <div class="section col web" id="landing-section">
            <div class="course-bgd web col">
                <Navbar :white="true" />
                <div class="course-landing row-wrap" v-if="course !== null">
                    <div class="course-info web">
                        <h1 class="title">{{ course.code }}</h1>
                        <h3 class="subtitle">{{ course.name }}</h3>
                    </div>
                    <CourseSummaryCard
                        :color="course.color"
                        :overallRating="overallRating"
                        :overallComment="overallComment"
                        :numReviews="visibleRating.numReviews"
                        :reviews="visibleRating.overallCount"
                        :professors="professors"
                        @updateProfessorFilter="updateProfessorFilter"
                        @updateRatings="updateRatings"
                        :professorFilter="professorFilter"
                    />
                </div>
            </div>
            <div class="course-bgd-footer web" />
        </div>

        <div class="section mobile">
            <div class="course-bgd mobile col" :style="mobileBackgroundStyles">
                <Navbar v-bind:white="true" @menu-click="onMenuClick" />
                <div class="course-info mobile" v-if="course !== null">
                    <h1 class="title">{{ course.code }}</h1>
                    <h3 class="subtitle">{{ course.name }}</h3>
                </div>
            </div>
            <div class="course-summary" :style="courseSummaryCardStyles">
                <CourseSummaryCard
                    v-if="course !== null"
                    color="course.color"
                    :overallRating="overallRating"
                    :overallComment="overallComment"
                    :numReviews="visibleRating.numReviews"
                    :reviews="visibleRating.overallCount"
                    :professors="professors"
                    @updateProfessorFilter="updateProfessorFilter"
                    @updateRatings="updateRatings"
                    :professorFilter="professorFilter"
                    :isMobile="true"
                />
            </div>
        </div>
        <div class="page-body-grid">
            <div class="page-body-item">
                <div class="section" id="stats-section" ref="stats-section" v-if="course !== null">
                    <div id="rating-banner">
                        <AddRatingBanner :id="id" />
                    </div>
                    <div class="section-header row-wrap">
                        <h2>Deep dive review</h2>
                    </div>
                    <div class="col metric-group" v-if="course !== null">
                        <MetricCard
                            name="Workload"
                            :rating="visibleRating.ratingSums['workload'] / visibleRating.numReviews"
                        >
                            <MetricWorkload :hoursPerWeek="hoursPerWeek" :hoursBreakdown="hoursBreakdown" />
                        </MetricCard>
                        <MetricCard
                            name="Relevance"
                            :rating="visibleRating.ratingSums['relevance'] / visibleRating.numReviews"
                        >
                            <MetricRelevance :courseTitle="course.code + ' ' + course.name" :data="course.jobTags" />
                        </MetricCard>
                        <MetricCard
                            name="Professor"
                            :rating="visibleRating.ratingSums['professor'] / visibleRating.numReviews"
                        >
                            <MetricProfessor
                                :name="professorFilter"
                                :reviews="reviewsToRender"
                                v-if="reviewsToRender !== null && reviewsToRender.length > 0"
                            />
                        </MetricCard>
                        <MetricCard
                            name="Difficulty"
                            :rating="visibleRating.ratingSums['difficulty'] / visibleRating.numReviews"
                        >
                            <MetricDifficulty :reviews="reviews" v-if="reviews !== null && reviews.length > 0" />
                        </MetricCard>
                    </div>
                </div>
                <div class="section" ref="reviews-section" id="reviews-section">
                    <div class="section-header row-wrap">
                        <h2>Reviews from course alumni</h2>
                    </div>
                    <div id="reviews-group" v-if="reviews">
                        <QualitativeReview
                            v-for="review in reviewsToRender"
                            :key="review.id"
                            :appreciations="20"
                            :userName="review.name"
                            :userPhoto="review.userPhoto"
                            :gpa="review.overall.grade"
							:hours="review.hours"
                            :date="review.date"
                            :timeTaken="review.quarter + ' ' + review.year"
                            :yearTaken="parseToInt(review.year)"
                            :professor="review.professor"
                            :overallRating="review.rating.overall"
                            :workloadRating="review.rating.workload"
                            :difficultyRating="review.rating.difficulty"
                            :professorRating="review.rating.professor"
                            :relevanceRating="review.rating.relevance"
                            :review="review.text"
                            :tips="review.tips"
                            :showReviews="showReviews"
                            :showTips="showTips"
                            :version="review.email ? 2 : 1"
                        />
                    </div>
                </div>
            </div>
            <div v-if="course !== null">
                <nav id="side-navbar" role="navigation" aria-label="main navigation">
                    <div class="side-navbar-top">
                        <div class="side-navbar-top-header">
                            <h4 class="nav-title">{{ course.code }}</h4>
                            <span class="ratings">
                                <img src="@/assets/icons/other/star-gold.svg" /> {{ this.overallRating.toFixed(2) }}
                            </span>
                        </div>
                        <p class="num-ratings">based on {{ visibleRating.numReviews }} ratings</p>
                        <p class="filter-instructions">Filter to see a different rating</p>
                        <div class="prof-button">
                            <button @click="openProfessorsModal">
                                {{ professorFilter === "all" ? "All professors" : professorFilter }}
                                <i class="fas fa-chevron-down"></i>
                            </button>
                        </div>
                    </div>
                    <ol class="page-sections">
                        <li @click="scrollTo('stats-section')" value="1">Metrics <i class="fas fa-circle"></i></li>
                        <li value="2">Logistics <i class="fas fa-circle"></i></li>
                        <li @click="scrollTo('reviews-section')" value="3">
                            Alumni reviews <i class="fas fa-circle"></i>
                        </li>
                        <div class="switch-containers">
                            <div>
                                <label>Show tips</label>
                                <div class="field">
                                    <input
                                        id="tipsSwitch"
                                        type="checkbox"
                                        class="switch is-rounded is-rtl is-info"
                                        :checked="showTips ? 'checked' : ''"
                                        @change="toggleShowTips"
                                    />
                                    <label for="tipsSwitch"></label>
                                </div>
                            </div>
                            <div>
                                <label>Show reviews</label>
                                <div class="field">
                                    <input
                                        id="reviewsSwitch"
                                        type="checkbox"
                                        class="switch is-rounded is-rtl is-info"
                                        :checked="showReviews ? 'checked' : ''"
                                        @change="toggleShowReviews"
                                    />
                                    <label for="reviewsSwitch"></label>
                                </div>
                            </div>
                        </div>
                    </ol>
                    <ul class="actions">
                        <router-link :to="'/rate/' + this.$router.currentRoute.params.course">
                            <li class="add-rating"><i class="fas fa-plus-circle"></i> Add a rating</li>
                        </router-link>
                        <li @click="scrollTo('app')" class="to-top"><i class="fas fa-arrow-up"></i> Back to top</li>
                    </ul>
                </nav>
            </div>
            <div class="mobile-filter-reviews">
                <button @click="toggleReviewsModal"><i class="fas fa-filter"></i>Filter Reviews</button>
                <div class="modal" v-bind:class="{ 'is-active': showReviewsModal }">
                    <div class="modal-background"></div>
                    <div class="modal-card">
                        <header class="modal-card-head">
                            <p class="modal-card-title">Filter Reviews</p>
                            <button class="delete" aria-label="close" @click="toggleReviewsModal">
                                <img src="@/assets/icons/actions/close-icon.svg" />
                            </button>
                        </header>
                        <section class="modal-card-body">
                            <div class="switch-containers">
                                <div>
                                    <label>Show tips</label>
                                    <div class="field">
                                        <input
                                            id="tipsSwitchMobile"
                                            type="checkbox"
                                            class="switch is-rounded is-rtl is-info"
                                            :checked="showTips ? 'checked' : ''"
                                            @change="toggleShowTips"
                                        />
                                        <label for="tipsSwitchMobile"></label>
                                    </div>
                                </div>
                                <div>
                                    <label>Show reviews</label>
                                    <div class="field">
                                        <input
                                            id="reviewsSwitchMobile"
                                            type="checkbox"
                                            class="switch is-rounded is-rtl is-info"
                                            :checked="showReviews ? 'checked' : ''"
                                            @change="toggleShowReviews"
                                        />
                                        <label for="reviewsSwitchMobile"></label>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>

        <div id="feedback-section">
            <FeedbackBanner />
        </div>

        <Footer />
    </div>
</template>

<script>
// Components
import Navbar from "@/components/navigation/Navbar";
import Footer from "@/components/navigation/Footer";
import AddRatingBanner from "@/components/banners/AddRatingBanner";
import FeedbackBanner from "@/components/banners/FeedbackBanner";
import MetricCard from "@/components/cards/MetricCard";
import MetricWorkload from "@/components/cards/MetricWorkloadCard";
import MetricRelevance from "@/components/cards/MetricRelevanceCard";
import MetricProfessor from "@/components/cards/MetricProfessorCard";
import MetricDifficulty from "@/components/cards/MetricDifficultyCard";
import CourseSummaryCard from "@/components/cards/CourseSummaryCard";
import QualitativeReview from "@/components/other/QualitativeReview";

// Libraries
import { firestore } from "@/firebase.js";
import Vue from "vue";

// Mixins
import utilMixin from "../mixins/utilMixin";

let handleScroll = () => {
    let fromTop = window.scrollY;
    let mainNavLinks = document.querySelectorAll(".page-sections li");
    let hashes = {
        "1": "#stats-section",
        "3": "#reviews-section",
    };

    mainNavLinks.forEach((link) => {
        let name = hashes[link.value + ""];
        let section = document.querySelector(name);
        if (section !== null) {
            if (section.offsetTop <= fromTop && section.offsetTop + section.offsetHeight > fromTop) {
                link.classList.add("active");

                if (name === "#reviews-section") {
                    document.querySelector(".switch-containers").classList.add("active");
                    document.querySelector(".mobile-filter-reviews").classList.add("active");
                } else if (name === "#stats-section") {
                    document.querySelector("#side-navbar").classList.add("active");
                }
            } else {
                link.classList.remove("active");

                if (name === "#reviews-section") {
                    document.querySelector(".switch-containers").classList.remove("active");
                    document.querySelector(".mobile-filter-reviews").classList.remove("active");
                } else if (name === "#stats-section") {
                    document.querySelector("#side-navbar").classList.remove("active");
                }
            }
        }
    });
};

Vue.directive("scroll", {
    bind: function() {
        window.addEventListener("scroll", handleScroll);
    },
});

export default {
    // This is the name of our component
    name: "CoursePage",

    // This is all of the components that we use
    components: {
        Navbar,
        Footer,
        CourseSummaryCard,
        MetricCard,
        MetricWorkload,
        MetricRelevance,
        MetricProfessor,
        MetricDifficulty,
        QualitativeReview,
        AddRatingBanner,
        FeedbackBanner,
    },

    mixins: [utilMixin],

    mounted() {
        let course = this.$router.currentRoute.params.course;
        this.id = course;
        let coursesStore = firestore
            .collection("university")
            .doc("uw")
            .collection("courses");

        // Get this course
        coursesStore
            .doc(course)
            .get()
            .then((doc) => {
                // We got the data for this course!
                if (doc.exists) {
                    this.course = doc.data();
                    this.updateRatings(null);

                    // Make sure we have course data for each professor
                    this.course.professors.forEach((profId) => {
                        // Add all the professors
                        firestore
                            .collection("university")
                            .doc("uw")
                            .collection("professors")
                            .doc(profId)
                            .get()
                            .then((profDoc) => {
                                if (profDoc.exists) {
                                    this.professors.push(profDoc.data());
                                }
                            });
                    });
                }

                // This course doesn't exist
                else {
                    console.warn("No such document!");
                }
            })
            .catch((error) => {
                // Firebase could not connect
                console.warn("Error getting document:", error);
            });

        // Get all the reviews
        coursesStore
            .doc(course)
            .collection("reviews")
            .get()
            .then((snapshot) => {
                snapshot.forEach((doc) => {
                    let data = doc.data();
                    this.reviews.push(data);
                });
            });
    },

    computed: {
        // styles
        courseSummaryCardStyles() {
            if (this.menuOpen)
                return {
                    "margin-top": "300px",
                };
            return {
                "margin-top": "220px",
            };
        },
        backgroundStyles() {
            return { "--bgd-color": this.course ? this.course.color : "#FFFFFF" };
        },
        mobileBackgroundStyles() {
            return {
                "min-height": this.menuOpen ? "350px" : "300px",
            };
        },
        // reviews
        reviewsToRender() {
            return this.reviews.filter((review) => {
                if (this.professorFilter !== "all") {
                    return (review.text != "" || review.tips != "") && review.professor === this.professorFilter;
                }
                return review.text != "" || review.tips != "";
            });
        },
        // descriptions / labels
        overallComment() {
            return this.course.quote ? this.course.quote : undefined;
        },
        overallRating() {
            return this.visibleRating.ratingSums["overall"] / this.visibleRating.numReviews;
        },

        hoursPerWeek() {
            let avgHours = this.visibleRating.hoursSum / this.visibleRating.numReviews;
            avgHours = Math.round(avgHours);
            return [Math.max(avgHours - 1, 0), avgHours + 1];
        },

        hoursBreakdown() {
            let avgHours = this.visibleRating.hoursSum / this.visibleRating.numReviews;
            let dist = this.visibleRating.workDistribution;

            // Find the sum of hours
            let total = 0;
            for (let key in dist) {
                total += dist[key];
            }

            // Normalize with avgHours
            for (let key in dist) {
                let time = (avgHours * dist[key]) / total;
                if (time > 0) {
                    dist[key] = Math.max(1, Math.round(time));
                }
            }

            return dist;
        },
    },

    // Get the current class from the link
    data() {
        return {
            id: null,
            course: null,
            visibleRating: null,
            professors: [],
            reviews: [],

            menuOpen: false,
            onMenuClick: this.toggleMenuStyles,

            professorFilter: "all",

            showTips: true,
            showReviews: true,
            showReviewsModal: false,
        };
    },

    methods: {
        toggleMenuStyles(isOpen) {
            this.menuOpen = isOpen;
        },

        updateProfessorFilter(value) {
            this.professorFilter = value;
        },

        updateRatings(value) {
            // All professors was selected
            if (value === null) {
                this.visibleRating = this.course;
            } else {
                this.visibleRating = this.professors[value];
            }
        },

        toggleShowTips() {
            this.showTips = !this.showTips;
        },

        toggleShowReviews() {
            this.showReviews = !this.showReviews;
        },

        toggleReviewsModal() {
            this.showReviewsModal = !this.showReviewsModal;
        },

        openProfessorsModal() {
            this.$emit("openModal");
        },

        scrollTo(sectionId) {
            let element = this.$refs[sectionId];
            let top = element.offsetTop;

            window.scrollTo(0, top);
        },
    },

    destroyed() {
        window.removeEventListener("scroll", handleScroll);
    },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_main.scss";

.section {
    padding: 25px 8%;

    &.mobile {
        padding: 0;
        margin: 0;
    }
}

.section-header {
    padding-top: 25px;
    padding-bottom: 25px;
    margin: -24px 0 0 0;

    > * {
        margin-top: 24px;
    }

    h2 {
        font-size: 28px;
        flex: 1;
        min-width: fit-content;
    }
}

#landing-section {
    align-items: flex-end;
    min-height: 500px;
    padding: 0rem;
    margin: 0px;
    width: 100%;
}

.course-bgd {
    height: fit-content;
    background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.14) 0%,
            rgba(255, 255, 255, 0.08) 13.02%,
            rgba(255, 255, 255, 0) 25%,
            rgba(255, 255, 255, 0) 79.69%,
            rgba(255, 255, 255, 0.08) 90.1%,
            rgba(255, 255, 255, 0.14) 100%
        )
        var(--bgd-color) top no-repeat;
    padding: 0 8%;

    &.web {
        width: 84%;
        min-height: 550px;
        border-radius: 0 0 5rem 5rem;
    }

    &.mobile {
        width: calc(100% - 50px);
        position: absolute;
        top: 0;
        margin: 0;
        padding: 0 25px;
        height: 300px;
        border-radius: 0 0 2rem 2rem;
    }
}

.course-landing {
    flex: 1;
    align-items: center;
}

.course-info {
    > * {
        color: white;
    }

    &.web {
        flex: 1;

        h1 {
            font-weight: 600;
        }
    }

    &.mobile {
        max-width: calc(100% - 25px);
        width: 300px;

        h3 {
            line-height: 24px;
        }

        p {
            line-height: 0;
        }
    }
}

.course-summary {
    position: relative;
    z-index: 20;
    padding: 0 25px;
}

.course-bgd-footer {
    background: url("../assets/icons/other/code-icon.svg") repeat;
    height: 200px;
    width: 100%;
    opacity: 0.1;
    margin-top: -150px;
}

.page-body-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}

.page-body-item {
    grid-column-start: 1;
    grid-column-end: 3;
}

.mobile-filter-reviews {
    display: none;
}

#side-navbar {
    background-color: #fafafa;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    padding: 24px;
    width: 60%;
    position: -webkit-sticky;
    position: sticky;
    top: 24px;
    z-index: 10;

    .filter-instructions {
        display: none;
    }

    .side-navbar-top {
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);

        .nav-title {
            display: inline;
            margin-right: 13px;
        }

        .ratings {
            font-size: 20px;
            color: #e3c855;

            img {
                height: 15px;
            }
        }

        .num-ratings {
            color: #999999;
            font-size: 16px;
            margin: 5px 0;
        }

        .prof-button {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 24px 0;

            button {
                color: #999999;
                background-color: #f4e5de;
                border: none;
                height: 40px;
                width: 80%;
                border-radius: 99px;
                font-size: 16px;
            }
        }
    }

    .page-sections {
        font-size: 16px;
        padding: 0;

        .switch-containers {
            border-left: 1px solid #999999;
            padding-left: 14px;
            display: none;

            input {
                margin-top: 16px;
            }

            &.active {
                display: block;
            }

            & > div {
                display: flex;
                justify-content: space-between;

                & > label {
                    color: #999999;
                }

                &:nth-child(2) {
                    margin-top: 14px;
                }
            }
        }

        li {
            list-style-type: none;
            margin: 24px 0;

            a {
                text-decoration: none;
                color: black;
            }

            &:hover {
                cursor: pointer;
            }

            &.active {
                font-weight: 900;

                .fa-circle {
                    display: inline;
                    width: 6px;
                    color: #e3c855;
                    margin-left: 12px;
                }
            }

            .fa-circle {
                display: none;
            }
        }
    }

    .actions {
        font-size: 16px;
        padding: 0;

        a {
            text-decoration: none;
        }

        li {
            list-style-type: none;
            margin: 12px 0;

            &.add-rating {
                color: $primary;
            }

            &.to-top {
                color: #999999;
            }

            &:hover {
                cursor: pointer;
            }
        }

        .fa-plus-circle,
        .fa-arrow-up {
            margin-right: 8px;
        }
    }
}

// Color Bulma switches a custom color
.switch[type="checkbox"].is-info:checked + label::before,
.switch[type="checkbox"].is-info:checked + label:before {
    background: $primary;
}

#stats-section {
    .card-group {
        display: grid;
        grid-gap: 1rem;
        grid-template-columns: repeat(auto-fill, minmax(308px, 1fr));
        grid-auto-rows: 1fr 1fr;
    }

    .section-header {
        padding: 0;
        margin: 80px 0 50px 0;
    }
}

.metric-group {
    > * {
        margin: 18px 0;
    }
}

#feedback-section {
    padding: 80px 10%;
}

@media only screen and (max-width: $breakpoint-small) {
    .section-header {
        padding-bottom: 12px;

        & a {
            display: none;
        }

        h2 {
            font-weight: bold;
            font-size: 18px;
        }
    }

    #reviews-group > :nth-child(1) {
        padding-top: 0;
    }

    .metric-group {
        > * {
            margin: 12px 0;
        }
    }

    #stats-section {
        .section-header {
            margin: -24px 0 21px 0;
        }
    }

    #rating-banner {
        padding-bottom: 25px;
    }
}

@media only screen and (max-width: $breakpoint-medium) {
    .card-group {
        justify-content: center;
    }

    .section {
        padding: 25px;
    }
}

@media only screen and (max-width: $breakpoint-large) {
    .page-body-grid {
        display: block;
    }

    #side-navbar {
        background-color: rgba(243, 239, 235, 0.95);
        border-radius: 0;
        border-bottom: none;
        padding: 0;
        position: fixed;
        margin: 0;
        top: 0;
        width: 100%;
        display: none;

        &.active {
            display: block;
        }

        .side-navbar-top {
            border-bottom: none;
        }

        ol,
        ul {
            display: none;
        }

        .filter-instructions {
            display: inline;
            margin-left: 24px;
            color: #999999;
        }

        .side-navbar-top-header {
            margin-left: 24px;
            margin-top: 24px;
        }

        .num-ratings {
            display: none;
        }
    }

    .mobile-filter-reviews {
        display: none;
        justify-content: center;
        position: fixed;
        bottom: 0;
        width: 100%;
        background-color: rgba(243, 239, 235, 0.95);
        padding: 20px 18px;

        &.active {
            display: flex;
        }

        & > button {
            border-radius: 99px;
            height: 38px;
            background-color: $light;
            color: #999999;
            width: 80%;
            border: none;
            font-size: 14px;

            .fa-filter {
                margin-right: 8px;
            }
        }
    }

    .modal-card {
        border-radius: 0;
        max-height: none;
        padding: 0;
        height: 100%;
        width: 100%;

        section.modal-card-body {
            border-radius: 0px;
        }

        .modal-card-head {
            border-radius: 0px;
            background-color: white;
            border-bottom: none !important;
            padding-bottom: 0;
        }

        button.delete {
            align-items: center;
            border-radius: 100%;
            background-color: $lighter;
            display: flex;
            height: 33px;
            justify-content: center;
            width: 33px;
        }

        .switch-containers {
            display: block input {
                margin-top: 20px;
            }

            & > div {
                display: flex;
                justify-content: space-between;

                & > label {
                    color: #999999;
                }

                &:nth-child(2) {
                    margin-top: 14px;
                }
            }
        }
    }
}
</style>
